import { useState, useEffect, useMemo } from 'react';
import { getCategoryConfig, getAllCategoryConfigs } from '../config/categoryConfigs';
import { getCategoryDataSource, getCategoryFilterKey } from '../utils/categoryUtils';

/**
 * Hook for managing category state and configuration
 */
export const useCategory = (categoryType) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const config = useMemo(() => {
    try {
      return getCategoryConfig(categoryType);
    } catch (err) {
      setError(`Invalid category type: ${categoryType}`);
      return null;
    }
  }, [categoryType]);

  const dataSource = useMemo(() => {
    return getCategoryDataSource(categoryType);
  }, [categoryType]);

  const filterKey = useMemo(() => {
    return getCategoryFilterKey(categoryType);
  }, [categoryType]);

  useEffect(() => {
    if (config) {
      setIsLoading(false);
      setError(null);
    } else if (categoryType) {
      setError(`Category configuration not found for type: ${categoryType}`);
      setIsLoading(false);
    }
  }, [config, categoryType]);

  return {
    config,
    dataSource,
    filterKey,
    isLoading,
    error,
    isValid: !error && !!config
  };
};

/**
 * Hook for managing category services data
 */
export const useCategoryServices = (categoryType, filters = {}) => {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 12,
    total: 0,
    totalPages: 0
  });

  const { config, dataSource, filterKey } = useCategory(categoryType);

  // Mock data fetching - replace with actual API call
  useEffect(() => {
    const fetchServices = async () => {
      if (!config || !dataSource) return;

      try {
        setIsLoading(true);
        setError(null);

        // Mock API call - replace with actual service
        await new Promise(resolve => setTimeout(resolve, 500)); // Simulate loading

        // Mock services data - replace with actual data from API
        const mockServices = [
          {
            id: `${filterKey}-1`,
            title: `Premium ${config.displayName} Service`,
            description: `Professional ${config.displayName.toLowerCase()} ceremony with experienced pandits`,
            price: 1500,
            duration: '2-3 hours',
            rating: 4.8,
            reviewCount: 124,
            image: config.image,
            features: config.features,
            category: filterKey
          },
          {
            id: `${filterKey}-2`,
            title: `Standard ${config.displayName} Package`,
            description: `Traditional ${config.displayName.toLowerCase()} ritual with all necessary items`,
            price: 800,
            duration: '1-2 hours',
            rating: 4.6,
            reviewCount: 89,
            image: config.image,
            features: config.features.slice(0, 2),
            category: filterKey
          },
          {
            id: `${filterKey}-3`,
            title: `Basic ${config.displayName} Service`,
            description: `Essential ${config.displayName.toLowerCase()} ceremony for small gatherings`,
            price: 500,
            duration: '1 hour',
            rating: 4.4,
            reviewCount: 67,
            image: config.image,
            features: [config.features[0]],
            category: filterKey
          }
        ];

        setServices(mockServices);
        setPagination({
          page: 1,
          pageSize: 12,
          total: mockServices.length,
          totalPages: Math.ceil(mockServices.length / 12)
        });
      } catch (err) {
        setError(`Failed to load ${config.displayName.toLowerCase()} services: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchServices();
  }, [config, dataSource, filterKey, filters]);

  const refetch = () => {
    if (config && dataSource) {
      setIsLoading(true);
      // Trigger re-fetch
    }
  };

  return {
    services,
    isLoading,
    error,
    pagination,
    refetch
  };
};

/**
 * Hook for category navigation and routing
 */
export const useCategoryNavigation = () => {
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);

  useEffect(() => {
    const allCategories = getAllCategoryConfigs().map(config => ({
      type: config.type,
      name: config.name,
      displayName: config.displayName,
      icon: config.icon,
      path: `/services/categories/${config.name}`,
      priority: config.getMetadata('priority'),
      popularityScore: config.getMetadata('popularityScore')
    }));

    setCategories(allCategories.sort((a, b) => a.priority - b.priority));
  }, []);

  const navigateToCategory = (categoryType) => {
    const config = getCategoryConfig(categoryType);
    if (config) {
      setCurrentCategory({
        type: config.type,
        name: config.name,
        displayName: config.displayName,
        path: `/services/categories/${config.name}`
      });
      return `/services/categories/${config.name}`;
    }
    return null;
  };

  const getNextCategory = (currentCategoryType) => {
    const currentIndex = categories.findIndex(cat => cat.type === currentCategoryType);
    if (currentIndex >= 0 && currentIndex < categories.length - 1) {
      return categories[currentIndex + 1];
    }
    return categories[0]; // Loop back to first
  };

  const getPreviousCategory = (currentCategoryType) => {
    const currentIndex = categories.findIndex(cat => cat.type === currentCategoryType);
    if (currentIndex > 0) {
      return categories[currentIndex - 1];
    }
    return categories[categories.length - 1]; // Loop to last
  };

  return {
    categories,
    currentCategory,
    navigateToCategory,
    getNextCategory,
    getPreviousCategory
  };
};

/**
 * Hook for category filtering and search
 */
export const useCategoryFilters = (categoryType) => {
  const [filters, setFilters] = useState({
    priceRange: { min: 0, max: 5000 },
    duration: 'all',
    rating: 0,
    features: [],
    sortBy: 'popularity'
  });

  const { config } = useCategory(categoryType);

  const updateFilter = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      priceRange: { min: 0, max: 5000 },
      duration: 'all',
      rating: 0,
      features: [],
      sortBy: 'popularity'
    });
  };

  const getFilterOptions = () => {
    if (!config) return {};

    return {
      priceRange: {
        enabled: config.getCustomization('enablePriceComparison'),
        min: 0,
        max: 5000,
        step: 100
      },
      duration: {
        enabled: true,
        options: [
          { value: 'all', label: 'Any Duration' },
          { value: '1hr', label: '1 Hour' },
          { value: '2hr', label: '1-2 Hours' },
          { value: '3hr', label: '2-3 Hours' },
          { value: '4hr+', label: '3+ Hours' }
        ]
      },
      rating: {
        enabled: config.getCustomization('showReviews'),
        options: [
          { value: 0, label: 'Any Rating' },
          { value: 4, label: '4+ Stars' },
          { value: 4.5, label: '4.5+ Stars' }
        ]
      },
      features: {
        enabled: true,
        options: config.features.map(feature => ({
          value: feature,
          label: feature.replace(/_/g, ' ').toLowerCase()
            .replace(/\b\w/g, l => l.toUpperCase())
        }))
      },
      sortBy: {
        enabled: true,
        options: [
          { value: 'popularity', label: 'Most Popular' },
          { value: 'price_low', label: 'Price: Low to High' },
          { value: 'price_high', label: 'Price: High to Low' },
          { value: 'rating', label: 'Highest Rated' },
          { value: 'duration', label: 'Duration' }
        ]
      }
    };
  };

  return {
    filters,
    updateFilter,
    resetFilters,
    getFilterOptions,
    hasActiveFilters: Object.values(filters).some(filter => 
      filter !== 'all' && filter !== 0 && 
      (!Array.isArray(filter) || filter.length > 0) &&
      !(typeof filter === 'object' && filter.min === 0 && filter.max === 5000)
    )
  };
};
